import { useEffect, useMemo, useRef, useState } from 'react';
import {
    PiArrowLeftBold,
    PiCrownDuotone,
    PiMapPinDuotone,
    PiShoppingCartDuotone,
    PiTrashDuotone,
} from 'react-icons/pi';

import { AddressFormBlock } from 'components/Form';
import Modal from 'components/Modal';
import FeedbackModal from 'components/FeedbackModal';
import { useAuth } from 'providers/AuthProvider';
import { useConfig } from 'providers/ConfigProvider';
import { useShop } from 'providers/ShopProvider';
import { getAddressString } from 'utils/GenericUtils';

const CUSTOM_ADDRESS = 'CUSTOM';
const ADDRESS_FIELD_NAME_MAP = {
    country: 'country',
    zipCode: 'zipCode',
    settlement: 'settlement',
    roadName: 'roadName',
    typesOfRoad: 'typesOfRoad',
    houseNumber: 'houseNumber',
    building: 'building',
    stairway: 'stairway',
    floor: 'floor',
    door: 'door',
};

const createAddressError = (message) => [{ message }];

export default function ShopBasketModal({ open, onClose, onRequireLogin, resumeCheckout = false }) {
    const {
        basket,
        basketItems,
        basketPointTotal,
        removeFromBasket,
        clearBasket,
        order,
        isBasketOrderCooldownActive,
    } = useShop();
    const { userData, redeemUserCredit, refreshUserData } = useAuth();
    const { getCombo } = useConfig();

    const [stage, setStage] = useState('basket');
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [customAddress, setCustomAddress] = useState(null);
    const [customAddressError, setCustomAddressError] = useState(null);
    const [orderError, setOrderError] = useState(null);
    const [loading, setLoading] = useState(false);
    const wasOpenRef = useRef(false);

    const formatAddress = (...values) => {
        try {
            return getAddressString(
                getCombo('country') || [],
                getCombo('settlement') || [],
                getCombo('typesOfRoad') || [],
                ...values
            );
        } catch {
            return null;
        }
    };

    const permanentAddress = userData
        ? formatAddress(
            userData.country,
            userData.postCode,
            userData.settlement,
            userData.roadName,
            userData.typesOfRoad,
            userData.houseNumber,
            userData.building,
            userData.floor,
            userData.door
        )
        : null;

    const temporaryAddress = userData
        ? formatAddress(
            userData.tempCountry,
            userData.tempPostCode,
            userData.tempSettlement,
            userData.tempRoadName,
            userData.tempTypesOfRoad,
            userData.tempHouseNumber,
            userData.tempBuilding,
            userData.tempFloor,
            userData.tempDoor
        )
        : null;

    const basketError = useMemo(() => {
        if (!userData || basket.length === 0) return null;

        const hasVipProduct = basketItems.some((item) => item.vip);
        const isVip = userData.vipExpiration !== null && userData.vipExpiration !== undefined;

        if (hasVipProduct && !isVip) {
            return 'A kosarad VIP terméket tartalmaz. A rendeléshez távolítsd el ezt a terméket, vagy rendelkezz aktív VIP tagsággal.';
        }

        if (basketPointTotal > Number(userData.credit || 0)) {
            return 'Nincs elegendő pontod a kosár teljes tartalmának megrendeléséhez.';
        }

        return null;
    }, [basket.length, basketItems, basketPointTotal, userData]);

    useEffect(() => {
        if (!open) {
            wasOpenRef.current = false;
            return;
        }

        if (wasOpenRef.current) return;
        wasOpenRef.current = true;

        const canResumeCheckout = resumeCheckout
            && !!userData
            && basket.length > 0
            && !basketError;

        setStage(
            isBasketOrderCooldownActive()
                ? 'cooldown'
                : canResumeCheckout ? 'address' : 'basket'
        );
        setSelectedAddress(null);
        setCustomAddress(null);
        setCustomAddressError(null);
        setOrderError(null);
        setLoading(false);
    }, [basket.length, basketError, open, resumeCheckout, userData, isBasketOrderCooldownActive]);

    const validateCustomAddress = () => {
        const errors = {};

        if (!customAddress?.country) errors.country = createAddressError('Kötelező megadni!');
        if (!customAddress?.zipCode) errors.zipCode = createAddressError('Kötelező megadni!');
        if (!customAddress?.settlement) errors.settlement = createAddressError('Hibás irányítószám!');
        if (!customAddress?.roadName) errors.roadName = createAddressError('Kötelező megadni!');
        if (!customAddress?.typesOfRoad) errors.typesOfRoad = createAddressError('Kötelező megadni!');
        if (!customAddress?.houseNumber) errors.houseNumber = createAddressError('Kötelező megadni!');

        setCustomAddressError(Object.keys(errors).length ? errors : null);
        return Object.keys(errors).length === 0;
    };

    const getSelectedAddressValue = () => {
        if (selectedAddress !== CUSTOM_ADDRESS) return selectedAddress;
        if (!validateCustomAddress()) return null;

        return formatAddress(
            customAddress.country,
            customAddress.zipCode,
            customAddress.settlement,
            customAddress.roadName,
            customAddress.typesOfRoad,
            customAddress.houseNumber,
            customAddress.building,
            customAddress.floor,
            customAddress.door
        );
    };

    const goToAddressSelection = () => {
        if (!userData) {
            onClose();
            onRequireLogin();
            return;
        }

        setStage('address');
    };

    const submitOrder = async () => {
        const shippingAddress = getSelectedAddressValue();
        if (!shippingAddress) return;

        setLoading(true);
        setOrderError(null);

        try {
            const redeemedPoints = basketPointTotal;
            await order(userData?.userToken, shippingAddress);

            clearBasket();
            redeemUserCredit(redeemedPoints);
            refreshUserData();
            setStage('success');
        } catch (error) {
            console.error('Basket order failed:', error);
            setOrderError(
                error.code === 'SHOP_ORDER_TOKEN_REQUIRED'
                    ? 'A bejelentkezett felhasználó adataiból hiányzik a rendeléshez szükséges userToken. Kérjük, jelentkezz be újra.'
                    : 'A rendelést nem sikerült elküldeni. Kérjük, próbáld újra később.'
            );
        } finally {
            setLoading(false);
        }
    };

    if (stage === 'cooldown' || stage === 'success') {
        const successful = stage === 'success';

        return (
            <FeedbackModal
                open={open}
                onClose={onClose}
                tone={successful ? 'success' : 'warning'}
                title={successful ? 'Sikeres pontbeváltás!' : 'Rendelés feldolgozás alatt'}
                message={successful
                    ? 'A termékek átvételének részleteiről kollégánk hamarosan felveszi veled a kapcsolatot.'
                    : 'Korábbi rendelésed feldolgozása még folyamatban van. Kérjük, várj néhány percet, mielőtt új rendelést adsz le.'}
                actionLabel="Vissza a shopba"
            />
        );
    }

    const title = {
        basket: 'Kosaram',
        address: 'Szállítási cím',
    }[stage];

    return (
        <Modal open={open} onClose={onClose} maxWidth="max-w-[520px]" className="!w-full" showClose={!loading}>
            <div className="flex flex-col">
                <div className="flex w-full items-center justify-between gap-4 px-5 py-4 text-white">
                    <div>
                        <h2 className="font-display text-2xl font-black leading-tight sm:text-3xl">{title}</h2>
                        {(stage === 'basket' || stage === 'address') && (
                            <p className="mt-1 text-xs font-semibold text-white/60">{basket.length} termék · {basketPointTotal} pont</p>
                        )}
                    </div>
                    <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-white/15 text-lime-300">
                        <PiShoppingCartDuotone className="size-7" />
                    </span>
                </div>

                <div className="mx-2 rounded-2xl bg-white p-4 text-neutral-900 shadow-2xl sm:p-5">
                    {stage === 'basket' && (
                        <BasketContent
                            items={basketItems}
                            pointTotal={basketPointTotal}
                            onRemove={removeFromBasket}
                        />
                    )}

                    {stage === 'address' && (
                        <div className="space-y-4">
                            <div>
                                <h3 className="font-display text-xl font-black">Hova érkezzen a rendelés?</h3>
                                <p className="mt-1 text-sm text-neutral-600">Válaszd ki a mentett címedet, vagy adj meg egy újat.</p>
                            </div>

                            {permanentAddress && (
                                <AddressOption
                                    title="Állandó lakcímem"
                                    address={permanentAddress}
                                    selected={selectedAddress === permanentAddress}
                                    onClick={() => setSelectedAddress(permanentAddress)}
                                />
                            )}

                            {temporaryAddress && temporaryAddress !== permanentAddress && (
                                <AddressOption
                                    title="Tartózkodási lakcímem"
                                    address={temporaryAddress}
                                    selected={selectedAddress === temporaryAddress}
                                    onClick={() => setSelectedAddress(temporaryAddress)}
                                />
                            )}

                            <AddressOption
                                title="Új címet adok meg"
                                address="Egyedi szállítási cím"
                                selected={selectedAddress === CUSTOM_ADDRESS}
                                onClick={() => setSelectedAddress(CUSTOM_ADDRESS)}
                            />

                            {selectedAddress === CUSTOM_ADDRESS && (
                                <div className="rounded-2xl bg-neutral-50 py-5">
                                    <AddressFormBlock
                                        open
                                        value={customAddress}
                                        error={customAddressError}
                                        fieldNameMap={ADDRESS_FIELD_NAME_MAP}
                                        onValueChange={setCustomAddress}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                </div>

                <div className="w-full px-4 pb-4 pt-3">
                    {basketError && <p className="mb-3 rounded-xl bg-red-500/15 px-4 py-3 text-sm font-semibold text-red-100">{basketError}</p>}
                    {orderError && <p className="mb-3 rounded-xl bg-orange-500/15 px-4 py-3 text-sm font-semibold text-orange-100">{orderError}</p>}

                    {stage === 'basket' && (
                        <button
                            type="button"
                            onClick={goToAddressSelection}
                            disabled={!!basketError || basket.length === 0}
                            className="gradient-yellow w-full rounded-xl py-3.5 font-black text-white transition hover:-translate-y-0.5 hover:brightness-110 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Tovább a rendeléshez
                        </button>
                    )}

                    {stage === 'address' && (
                        <div className="flex flex-col gap-3 sm:flex-row">
                            <button
                                type="button"
                                onClick={() => setStage('basket')}
                                disabled={loading}
                                className="flex w-full items-center justify-center gap-2 rounded-xl bg-white/15 py-3.5 font-black text-white transition hover:bg-white/25 disabled:opacity-50"
                            >
                                <PiArrowLeftBold /> Vissza
                            </button>
                            <button
                                type="button"
                                onClick={submitOrder}
                                disabled={loading || !selectedAddress || !!basketError}
                                className="gradient-yellow w-full rounded-xl py-3.5 font-black text-white transition hover:-translate-y-0.5 hover:brightness-110 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {loading ? 'Rendelés elküldése…' : 'Megrendelem'}
                            </button>
                        </div>
                    )}

                </div>
            </div>
        </Modal>
    );
}

function BasketContent({ items, pointTotal, onRemove }) {
    if (!items.length) {
        return (
            <StatusMessage
                Icon={PiShoppingCartDuotone}
                iconClassName="text-neutral-400"
                title="A kosarad üres"
                content="Böngéssz termékeink között, biztosan megtalálod a kedvenced."
            />
        );
    }

    return (
        <div className="space-y-2.5">
            {items.map((item) => (
                <article key={item.id} className="flex items-center gap-3 rounded-xl border border-neutral-100 bg-neutral-50 p-2.5">
                    <img
                        src={`${global.backingsEndpoint}photoRepository/getImageBySize?size=ORIGINAL&key=${item.photo}`}
                        alt={item.name}
                        className="size-14 shrink-0 rounded-lg object-cover sm:size-16"
                    />
                    <div className="min-w-0 flex-1">
                        <h3 className="font-display font-black text-neutral-900">{item.name}</h3>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs font-bold">
                            <span className="rounded-full bg-green-100 px-2.5 py-1 text-green-700">{item.pointsPrice} pont</span>
                            {item.vip && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-amber-700"><PiCrownDuotone /> VIP</span>}
                        </div>
                    </div>
                    <button type="button" onClick={() => onRemove(item.id)} aria-label={`${item.name} eltávolítása`} className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-500 transition hover:bg-red-100 hover:text-red-600">
                        <PiTrashDuotone className="size-5" />
                    </button>
                </article>
            ))}

            <div className="flex items-center justify-between rounded-xl bg-neutral-900 px-4 py-3 text-sm font-black text-white">
                <span>Összesen</span>
                <span>{pointTotal} pont</span>
            </div>
        </div>
    );
}

function AddressOption({ title, address, selected, onClick }) {
    return (
        <button type="button" onClick={onClick} className={`flex w-full items-center gap-3 rounded-2xl border p-4 text-left transition ${selected ? 'border-green-500 bg-green-50 ring-4 ring-green-100' : 'border-neutral-200 bg-white hover:border-green-300 hover:bg-green-50/50'}`}>
            <span className={`flex size-10 shrink-0 items-center justify-center rounded-xl ${selected ? 'gradient-green text-white' : 'bg-neutral-100 text-neutral-500'}`}>
                <PiMapPinDuotone className="size-6" />
            </span>
            <span className="min-w-0">
                <strong className="block font-display text-base font-black text-neutral-900">{title}</strong>
                <span className="block text-xs leading-relaxed text-neutral-600">{address}</span>
            </span>
        </button>
    );
}

function StatusMessage({ Icon, iconClassName, title, content }) {
    return (
        <div className="flex flex-col items-center px-3 py-6 text-center">
            <Icon className={`mb-3 size-14 ${iconClassName}`} />
            <h3 className="font-display text-xl font-black text-neutral-900">{title}</h3>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-neutral-600">{content}</p>
        </div>
    );
}
